import React from 'react';
import { CheckIcon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import { OrderStatus, PaymentStatus, STATUS_LABEL, PAYMENT_LABEL } from '../../data/orders';

/**
 * Fulfilment status. Deliberately separate from payment so a merchant can see
 * "Paid · In Progress" at a glance without decoding a single combined label.
 */
export function StatusChip({
  status,
  size = 'sm'
}: {
  status: OrderStatus;
  size?: 'sm' | 'lg';
}) {
  const tone: Record<OrderStatus, string> = {
    new: 'bg-primary-indigo text-white',
    progress: 'bg-primary-indigo/[0.08] text-primary-indigo',
    fulfilled: 'bg-success/10 text-success',
    cancelled: 'bg-slate-100 text-slate-500'
  };
  return <span className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider ${tone[status]} ${size === 'lg' ? 'px-3 py-1 text-xs' : 'px-2.5 py-1 text-[11px]'}`} data-id="element-2995" data-mp="1l8jc3a28t56z">
      {status === 'fulfilled' && <CheckIcon className="w-3 h-3" data-id="element-2996" data-mp="2wlvfxky05nt5" />}
      {STATUS_LABEL[status]}
    </span>;
}

/** Payment state — only a real problem gets warning colour. */
export function PaymentChip({
  payment
}: {
  payment: PaymentStatus;
}) {
  const tone: Record<PaymentStatus, string> = {
    paid: 'text-success',
    pending: 'text-warning',
    failed: 'text-error',
    refunded: 'text-slate-500'
  };
  const dot: Record<PaymentStatus, string> = {
    paid: 'bg-success',
    pending: 'bg-warning',
    failed: 'bg-error',
    refunded: 'bg-slate-300'
  };
  return <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${tone[payment]}`} data-id="element-2997" data-mp="1srg2eexddfex">
      <span className={`w-1.5 h-1.5 rounded-full ${dot[payment]}`} data-id="element-2998" data-mp="1ns3ia1xvumg1" />
      {PAYMENT_LABEL[payment]}
    </span>;
}

/** Quiet label: useful to know, never the thing you notice first. */
export function SourceLabel({
  source
}: {
  source: string;
}) {
  return <span className="text-[11px] font-semibold text-slate-400" data-id="element-2999" data-mp="1eqvj7hnjlhkw">{source}</span>;
}

/** Shown only when something genuinely needs the merchant. */
export function AttentionNote({
  kind
}: {
  kind: 'new' | 'payment' | 'outstanding' | 'none';
}) {
  if (kind === 'none') return null;
  if (kind === 'outstanding') return <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-warning" data-id="element-3000" data-mp="q6s9vz1vhdv6">
        <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-3001" data-mp="v5vdrg649e1k" />
        Payment still outstanding
      </span>;
  if (kind === 'payment') return <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-warning" data-id="element-3002" data-mp="1iwipkcj5knte">
        <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-3003" data-mp="1nuu1cst0d8e9" />
        Payment issue
      </span>;
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-primary-indigo" data-id="element-3004" data-mp="194mp4utlglm0">
      <ClockIcon className="w-3.5 h-3.5" data-id="element-3005" data-mp="1e3pj9k81d7f7" />
      Needs your attention
    </span>;
}